import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const AnalizeScreen = ({ user }) => {
  const navigate = useNavigate();
  const [timeFrame, setTimeFrame] = useState("1M");

  // Mock analytics data
  const portfolioStats = {
    totalValue: "$12,450.67",
    totalReturn: "+$1,234.56",
    returnPercent: "+11.02%",
    bestPerformer: "MSFT (+15.67%)",
    worstPerformer: "TSLA (-3.45%)",
    volatility: "12.34%",
    sharpeRatio: "1.23"
  };

  const allocationData = [
    { sector: "Technology", percentage: 45, value: "$5,602.80", color: "#3B82F6" },
    { sector: "Consumer Discretionary", percentage: 25, value: "$3,112.67", color: "#22D3EE" },
    { sector: "Healthcare", percentage: 15, value: "$1,867.60", color: "#22C55E" },
    { sector: "Financial", percentage: 10, value: "$1,245.07", color: "#F59E0B" },
    { sector: "Other", percentage: 5, value: "$622.53", color: "#EF4444" }
  ];

  const performanceHistory = [
    { date: "Jan", value: 10000 },
    { date: "Feb", value: 10500 },
    { date: "Mar", value: 11200 },
    { date: "Apr", value: 10800 },
    { date: "May", value: 11500 },
    { date: "Jun", value: 12450 }
  ];

  const timeFrames = ["1W", "1M", "3M", "6M", "1Y", "ALL"];

  if (!user) {
    return (
      <div className="screen">
        <div className="screen-header" style={{ marginTop: 56 }}>
          <h1 className="screen-title">Analize</h1>
        </div>
        <div className="card text-center" style={{ padding: "60px 0" }}>
          <span style={{ fontSize: "1.2rem", color: "#94A3B8" }}>
            🚧 Analize will be available soon.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-header" style={{ marginTop: 56 }}>
        <h1 className="screen-title">Portfolio Analysis</h1>
        <p className="screen-subtitle">Deep insights into your investment performance</p>
      </div>

      {/* Portfolio Overview */}
      <div className="portfolio-summary">
        <div className="portfolio-card">
          <div className="portfolio-value">{portfolioStats.totalValue}</div>
          <div className="portfolio-label">Total Value</div>
        </div>
        <div className="portfolio-card">
          <div className="portfolio-value" style={{ color: "#22C55E" }}>{portfolioStats.totalReturn}</div>
          <div className="portfolio-label">Total Return</div>
        </div>
        <div className="portfolio-card">
          <div className="portfolio-value" style={{ color: "#22C55E" }}>{portfolioStats.returnPercent}</div>
          <div className="portfolio-label">Return %</div>
        </div>
      </div>

      {/* Time Frame Selector */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Performance Timeline</h3>
        </div>
        <div className="flex gap-2" style={{ marginBottom: "20px" }}>
          {timeFrames.map((frame) => (
            <button
              key={frame}
              onClick={() => setTimeFrame(frame)}
              className={`btn ${timeFrame === frame ? "" : "btn-outline"} btn-small`}
            >
              {frame}
            </button>
          ))}
        </div>
        
        {/* Performance Chart Placeholder */}
        <div style={{ 
          height: "300px", 
          background: "#2A2A2A", 
          borderRadius: "8px", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          color: "#94A3B8",
          marginBottom: "20px"
        }}>
          📈 Performance chart for {timeFrame} will be displayed here
        </div>
      </div>

      {/* Asset Allocation */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Asset Allocation</h3>
        </div>
        <div className="grid-2">
          <div>
            {allocationData.map((item, index) => (
              <div key={index} style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center",
                padding: "12px 0",
                borderBottom: index < allocationData.length - 1 ? "1px solid #2A2A2A" : "none"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ 
                    width: "12px", 
                    height: "12px", 
                    borderRadius: "50%", 
                    backgroundColor: item.color 
                  }}></div>
                  <div>
                    <div style={{ fontWeight: "600", color: "#E5E7EB" }}>{item.sector}</div>
                    <div style={{ fontSize: "0.9rem", color: "#94A3B8" }}>{item.percentage}%</div>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: "600", color: "#E5E7EB" }}>{item.value}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ 
            height: "200px", 
            background: "#2A2A2A", 
            borderRadius: "8px", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            color: "#94A3B8"
          }}>
            🥧 Pie chart visualization
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Performance Metrics</h3>
        </div>
        <div className="grid-3">
          <div className="portfolio-card">
            <div className="portfolio-value">{portfolioStats.volatility}</div>
            <div className="portfolio-label">Volatility</div>
          </div>
          <div className="portfolio-card">
            <div className="portfolio-value">{portfolioStats.sharpeRatio}</div>
            <div className="portfolio-label">Sharpe Ratio</div>
          </div>
          <div className="portfolio-card">
            <div className="portfolio-value">1.23</div>
            <div className="portfolio-label">Beta</div>
          </div>
        </div>
      </div>

      {/* Top Performers */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Top Performers</h3>
        </div>
        <div className="grid-2">
          <div className="stock-item">
            <div className="stock-header">
              <div>
                <div className="stock-name">Best Performer</div>
                <div className="stock-symbol">{portfolioStats.bestPerformer}</div>
              </div>
              <div className="text-right">
                <div className="stock-change positive">+15.67%</div>
              </div>
            </div>
          </div>
          <div className="stock-item">
            <div className="stock-header">
              <div>
                <div className="stock-name">Worst Performer</div>
                <div className="stock-symbol">{portfolioStats.worstPerformer}</div>
              </div>
              <div className="text-right">
                <div className="stock-change negative">-3.45%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Analysis */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Risk Analysis</h3>
        </div>
        <div className="grid-2">
          <div>
            <h4 style={{ color: "#E5E7EB", marginBottom: "12px" }}>Risk Metrics</h4>
            <div style={{ color: "#94A3B8", lineHeight: "1.6" }}>
              <div>• Maximum Drawdown: -8.45%</div>
              <div>• Value at Risk (95%): -2.34%</div>
              <div>• Correlation with S&P 500: 0.87</div>
              <div>• Diversification Score: 7.2/10</div>
            </div>
          </div>
          <div>
            <h4 style={{ color: "#E5E7EB", marginBottom: "12px" }}>Recommendations</h4>
            <div style={{ color: "#94A3B8", lineHeight: "1.6" }}>
              <div>• Consider adding more international exposure</div>
              <div>• Rebalance portfolio monthly</div>
              <div>• Monitor technology sector concentration</div>
              <div>• Consider defensive stocks for stability</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalizeScreen; 